import type {
  AccountSubtypeName,
  AccountTypeName,
  AssetClassName,
  LiabilityTypeName,
  ProviderAccountSource,
} from 'src/gql/graphql';
import {
  AccountSubtypeName as AccountSubtypeNameEnum,
  AccountTypeName as AccountTypeNameEnum,
  AssetSubclassName,
} from 'src/gql/graphql';

import {
  ASSET_CLASS_COLLECTIBLE,
  ASSET_CLASS_COMPANY_EQUITY,
  ASSET_CLASS_INSURANCE_POLICY,
  ASSET_CLASS_OTHER,
  ASSET_CLASS_REAL_ESTATE,
  ASSET_CLASS_VEHICLE,
} from '@savvy/constants/asset-classes';
import {
  LIABILITY_TYPE_AUTO,
  LIABILITY_TYPE_COMMERCIAL,
  LIABILITY_TYPE_CONSUMER,
  LIABILITY_TYPE_GENERAL,
  LIABILITY_TYPE_HOME_EQUITY,
  LIABILITY_TYPE_LINE_OF_CREDIT,
  LIABILITY_TYPE_MORTGAGE,
  LIABILITY_TYPE_OTHER,
  LIABILITY_TYPE_STUDENT,
} from '@savvy/constants/liability-types';
import defaultGuard from '@savvy/utils/default-guard';

export const CLIENT_LINKED_PROVIDER_ACCOUNT_SOURCES: readonly ProviderAccountSource[] = ['PLAID', 'MONEYKIT'];

export const PLAID_OAUTH_AUTH_LOCAL_STORAGE_KEY = 'plaid-link-token';
export const PLAID_OAUTH_AUTH_REDIRECT_PAGE = 'plaid-oauth-redirect-page';

export const enum ItemType {
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  INVESTMENT_ACCOUNT = 'INVESTMENT_ACCOUNT',
  REAL_ESTATE = 'REAL_ESTATE',
  COMPANY_EQUITY = 'COMPANY_EQUITY',
  OTHER_ACCOUNT = 'OTHER_ACCOUNT',
  OTHER_ASSET = 'OTHER_ASSET',
  OTHER_LIABILITY = 'OTHER_LIABILITY',
}

export const ASSET_CLASS_DISPLAY_NAMES: Record<AssetClassName, string> = {
  [ASSET_CLASS_COLLECTIBLE]: 'Collectible',
  [ASSET_CLASS_COMPANY_EQUITY]: 'Company Equity',
  [ASSET_CLASS_INSURANCE_POLICY]: 'Insurance Policy',
  [ASSET_CLASS_REAL_ESTATE]: 'Real Estate',
  [ASSET_CLASS_VEHICLE]: 'Vehicle',
  [ASSET_CLASS_OTHER]: 'Other Asset',
};

export const ASSET_CLASS_MANUAL_ADD: AssetClassName[] = [
  ASSET_CLASS_INSURANCE_POLICY,
  ASSET_CLASS_COLLECTIBLE,
  ASSET_CLASS_VEHICLE,
  ASSET_CLASS_OTHER,
];

export const ASSET_SUBCLASS_DISPLAY_NAMES: Record<AssetSubclassName, string> = {
  [AssetSubclassName.InvestmentProperty]: 'Investment property',
  [AssetSubclassName.Other]: 'Other',
  [AssetSubclassName.PrimaryResidence]: 'Primary residence',
  [AssetSubclassName.SecondaryResidence]: 'Secondary residence',
  [AssetSubclassName.Boat]: 'Boat',
  [AssetSubclassName.Car]: 'Car',
  [AssetSubclassName.Antique]: 'Antique',
  [AssetSubclassName.Art]: 'Art',
  [AssetSubclassName.Jewelry]: 'Jewelry',
  [AssetSubclassName.Residence]: 'Residence',
  [AssetSubclassName.NonResidence]: 'Non-residence',
};

export const LIABILITY_TYPE_DISPLAY_NAME: Record<LiabilityTypeName, string> = {
  [LIABILITY_TYPE_AUTO]: 'Auto',
  [LIABILITY_TYPE_COMMERCIAL]: 'Commercial',
  [LIABILITY_TYPE_CONSUMER]: 'Consumer',
  [LIABILITY_TYPE_GENERAL]: 'General',
  [LIABILITY_TYPE_HOME_EQUITY]: 'Home Equity',
  [LIABILITY_TYPE_LINE_OF_CREDIT]: 'Line of Credit',
  [LIABILITY_TYPE_MORTGAGE]: 'Mortgage',
  [LIABILITY_TYPE_OTHER]: 'Other Liability',
  [LIABILITY_TYPE_STUDENT]: 'Student',
};
export function isLiabilityTypeName(type: string): type is LiabilityTypeName {
  return Object.hasOwn(LIABILITY_TYPE_DISPLAY_NAME, type);
}
export function isAccountTypeName(type: string): type is AccountTypeNameEnum {
  // @ts-expect-error - FIXME: This is using the wrong type
  return Object.values(AccountTypeNameEnum).includes(type);
}

export const LIABILITY_TYPES_MANUAL_ADD: LiabilityTypeName[] = [
  LIABILITY_TYPE_AUTO,
  LIABILITY_TYPE_COMMERCIAL,
  LIABILITY_TYPE_CONSUMER,
  LIABILITY_TYPE_LINE_OF_CREDIT,
  LIABILITY_TYPE_STUDENT,
  LIABILITY_TYPE_OTHER,
];

/**
 * @deprecated use the accountSubtypeDisplayName function instead
 */
export const DEPRECATED_ACCOUNT_SUBTYPE_DISPLAY_NAMES: Record<AccountSubtypeName | 'undefined' | 'null', string> = {
  beneficiary_ira: 'Beneficiary IRA',
  cash_management: 'Cash Management',
  cd: 'Certificate of Deposit',
  checking: 'Checking',
  company_daf: 'Company DAF',
  contributory_ira: 'Contributory IRA',
  corporation: 'Corporation',
  defined_benefit_plan: 'Defined Benefit Plan',
  education_ira: 'Education IRA',
  ebt: 'EBT',
  hsa: 'Health Savings Account',
  money_market: 'Money Market',
  paypal: 'Paypal',
  pledged_asset_line: 'Pledged asset line',
  prepaid: 'Prepaid',
  savings: 'Savings',
  credit_card: 'Credit card',
  paypal_credit_card: 'Paypal Credit Card',
  auto: 'Auto',
  business: 'Business',
  commercial: 'Commercial',
  construction: 'Construction',
  consumer: 'Consumer',
  home_equity: 'Home equity',
  line_of_credit: 'Line of Credit',
  loan: 'Loan',
  mortgage: 'Mortgage',
  other: 'Other',
  overdraft: 'Overdraft',
  student: 'Student',
  plan401a: '401a',
  plan401k: '401k',
  plan403b: '403b',
  plan457b: '457b',
  plan529: '529',
  brokerage: 'Brokerage',
  cash_isa: 'Cash ISA',
  contributory_roth: 'Contributory Roth',
  foundation: 'Foundation',
  education_savings_account: 'Education Savings Account',
  fixed_annuity: 'Fixed Annuity',
  gst_exempt: 'GST Exempt',
  gic: 'Guaranteed Investment Certificate (GIC)',
  health_reimbursement_arrangement: 'Health Reimbursement Arrangement',
  ira: 'IRA',
  ira_rollover: 'IRA Rollover',
  inherited_ira: 'Inherited IRA',
  isa: 'ISA',
  joint: 'Joint',
  joint_tenant: 'Joint Tenant',
  jtwros: 'JTWROS',
  keogh: 'Keogh',
  lif: 'Life Income Fund (LIF)',
  life_insurance: 'Life Insurance',
  lira: 'LIRA',
  lrif: 'LRIF',
  lrsp: 'LRSP',
  marital_trust: 'Marital Trust',
  mutual_fund: 'Mutual Fund',
  non_taxable_brokerage_account: 'Non-taxable Brokerage Account',
  other_annuity: 'Other Annuity',
  other_insurance: 'Other Insurance',
  pension: 'Pension',
  personal_choice_retirement_account: 'Personal Choice Retirement Account',
  private_equity: 'Private Equity',
  prif: 'PRIF',
  profit_sharing_plan: 'Profit Sharing Plan',
  qshr: 'QSHR',
  rdsp: 'RDSP',
  resp: 'RESP',
  retirement: 'Retirement',
  revocable_trust: 'Revocable Trust',
  rlif: 'RLIF',
  roth: 'Roth IRA',
  roth_401k: 'Roth 401k',
  rrif: 'RRIF',
  rrsp: 'RRSP',
  sarsep: 'SARSEP',
  sep_ira: 'SEP IRA',
  simple_ira: 'Simple IRA',
  sipp: 'SIPP',
  stock_plan: 'Stock Plan',
  tfsa: 'TTFA',
  trust: 'Trust',
  trust_under_will: 'Trust Under Will',
  ugma: 'UGMA',
  utma: 'UTMA',
  variable_annuity: 'Variable Annuity',
  hot_wallet: 'Hot wallet',
  cold_wallet: 'Cold wallet',
  hardware_wallet: 'Hardware Wallet',
  exchange: 'Exchange',
  private: 'Private',
  partnership: 'Partnership',
  public: 'Public',
  real_estate: 'Real Estate',
  spouse: 'Spouse',
  transfer_on_death: 'Transfer on Death',
  iso: 'ISO',
  nso: 'NSO',
  rsa: 'RSA',
  rsu: 'RSU',
  uniform_transfers_to_minors_account: 'Uniform Transfers to Minors Account',
  community_property: 'Community Property',
  tenants_by_entirety: 'Tenants by Entirety',
  tenants_in_common: 'Tenants in Common',
  conservatorship: 'Conservatorship',
  conversion_roth: 'Conversion Roth',
  custodial: 'Custodial',
  defined_contribution_plan: 'Defined Contribution Plan',
  defined_pension: 'Defined Pension',
  estate: 'Estate',
  guardianship: 'Guardianship',
  invalid_type: 'Other',
  money_purchase_pension_plan: 'Money Purchase Pension Plan',
  non_profit: 'Non-Profit',
  optional_retirement_program: 'Optional Retirement Program',
  retirement_plan: 'Retirement Plan',
  simplified_employee_pension_plan: 'Simplified Employee Pension Plan',
  sole_and_separate_property: 'Sole and Separate Property',
  tax_sheltered_account: 'Tax Sheltered Account',
  non_custodial_wallet: 'Non-Custodial Wallet',
  undefined: 'Other',
  null: 'Other',
  annuity: 'Annuity',
  corporate_401k: 'Corporate 401k',
  custodial_roth_contributory_ira: 'Custodial Roth Contributory IRA',
  defined: 'Defined',
  donor_advised_fund: 'Donor Advised Fund',
  educational_savings_account: 'Educational Savings Account',
  financial_planning_fee: 'Financial Planning Fee',
  health_savings_account: 'Health Savings Account',
  individual: 'Individual',
  inherited_roth_ira: 'Inherited Roth IRA',
  ira_cont: 'IRA Contribution',
  roth_ira: 'Roth IRA',
  simple: 'Simple',
  uniform_gift_to_minors_account: 'Uniform Gift to Minors Account',
};

/*
 * @deprecated use the otherAccountDisplayName function instead
 */
export const DEPRECATED_OTHER_ACCOUNT_DISPLAY_NAME: Record<
  AccountTypeNameEnum | 'undefined' | 'null',
  string | undefined | null
> = {
  [AccountTypeNameEnum.Depository]: 'Other bank account',
  [AccountTypeNameEnum.Investment]: 'Other investment account',
  [AccountTypeNameEnum.Credit]: 'Other credit account',
  [AccountTypeNameEnum.Insurance]: 'Other insurance account',
  [AccountTypeNameEnum.Cryptocurrency]: 'Other Crypto account',
  [AccountTypeNameEnum.Loan]: 'Other loan account',
  [AccountTypeNameEnum.Other]: 'Other',
  [AccountTypeNameEnum.EmployerEquity]: 'Other employer equity account',
  [AccountTypeNameEnum.FundInvestment]: 'Other fund investment account',
  [AccountTypeNameEnum.PrivateInvestment]: 'Other private investment account',
  undefined: 'Other',
  null: 'Other',
};
export function otherAccountDisplayName(accountTypeName: AccountTypeName | null | undefined) {
  switch (accountTypeName) {
    case 'depository':
      return 'Other bank account';
    case 'investment':
      return 'Other investment account';
    case 'credit':
      return 'Other credit account';
    case 'insurance':
      return 'Other insurance account';
    case 'cryptocurrency':
      return 'Other Crypto account';
    case 'loan':
      return 'Other loan account';
    case 'other':
      return 'Other';
    case 'employer_equity':
      return 'Other employer equity account';
    case 'fund_investment':
      return 'Other fund investment account';
    case 'private_investment':
      return 'Other private investment account';
    case undefined:
    case null:
      return 'Other';
    default:
      return defaultGuard(accountTypeName, 'Other');
  }
}

export const REAL_ESTATE_SUBCLASSES_MANUAL_ADD = [AssetSubclassName.Residence, AssetSubclassName.NonResidence];

// TODO: auto-generate groupings from the backend
export const DEPOSITORY_SUB_TYPES_MANUAL_ADD = [
  AccountSubtypeNameEnum.Checking,
  AccountSubtypeNameEnum.Savings,
  AccountSubtypeNameEnum.MoneyMarket,
  AccountSubtypeNameEnum.Cd,
  AccountSubtypeNameEnum.Hsa,
  AccountSubtypeNameEnum.Other,
];

export const CREDIT_SUBTYPES = [AccountSubtypeNameEnum.CreditCard, AccountSubtypeNameEnum.PaypalCreditCard];

export const LOAN_SUBTYPES = [
  AccountSubtypeNameEnum.Auto,
  AccountSubtypeNameEnum.Business,
  AccountSubtypeNameEnum.Commercial,
  AccountSubtypeNameEnum.Construction,
  AccountSubtypeNameEnum.Consumer,
  AccountSubtypeNameEnum.HomeEquity,
  AccountSubtypeNameEnum.LineOfCredit,
  AccountSubtypeNameEnum.Loan,
  AccountSubtypeNameEnum.Mortgage,
  AccountSubtypeNameEnum.Other,
  AccountSubtypeNameEnum.Overdraft,
  AccountSubtypeNameEnum.Student,
];

export const INVESTMENT_SUB_TYPES_MANUAL_ADD = [
  AccountSubtypeNameEnum.Plan401k,
  AccountSubtypeNameEnum.Brokerage,
  AccountSubtypeNameEnum.Ira,
  AccountSubtypeNameEnum.Roth,
  AccountSubtypeNameEnum.MutualFund,
  AccountSubtypeNameEnum.Plan529,
  AccountSubtypeNameEnum.Plan403b,
  AccountSubtypeNameEnum.Other,
];
